import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { customersAPI } from "../../services";
import "../../style/customerDetails.css";

const EditFinanceTransactionForm = ({ transaction, customerId, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    date: '',
    invoiceId: '',
    type: 'Credit',
    details: '',
    amount: '',
    credit: '',
    debit: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(customerId || (transaction?.customerId || ''));

  const isEditing = !!transaction;

  useEffect(() => {
    if (transaction) {
      const isCredit = transaction.type === 'Credit';
      const isDebit = transaction.type === 'Debit';
      const amount = transaction.amount || 0;
      
      setFormData({
        date: transaction.date || '',
        invoiceId: transaction.invoiceId || transaction.id || '',
        type: transaction.type || 'Credit',
        details: transaction.details || '',
        amount: transaction.amount || '',
        credit: isCredit ? `${Math.abs(amount)}` : '',
        debit: isDebit ? `${Math.abs(amount)}` : ''
      });
      setSelectedCustomerId(transaction.customerId || customerId || '');
    } else {
      // Set default date to today for new transactions
      const today = new Date().toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        date: today
      }));
    }
  }, [transaction, customerId]);

  // Load customers for dropdown
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const result = await customersAPI.getCustomers('', 1, 50);
        const raw = result?.response ?? result;
        const list = Array.isArray(raw) ? raw : (raw?.results ?? raw?.data ?? []);
        const options = (list || [])
          .map(c => ({ id: c.id ?? c.customer_id ?? c.pk, name: c.name ?? c.customer_name ?? c.user_name ?? 'Unknown' }))
          .filter(o => o.id);
        setCustomerOptions(options);
      } catch (e) {
        console.error('Failed to load customers for dropdown', e);
      }
    };
    loadCustomers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };

      // Implement mutual exclusivity: if credit has value, clear debit and vice versa
      if (name === 'credit' && value.trim() && value !== "0.0" && value !== "0") {
        newData.debit = "";
        newData.type = 'Credit';
      } else if (name === 'debit' && value.trim() && value !== "0.0" && value !== "0") {
        newData.credit = "";
        newData.type = 'Debit';
      }

      return newData;
    });

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.date.trim()) {
      newErrors.date = 'Date is required';
    }

    if (!selectedCustomerId) {
      newErrors.customer = 'Customer is required';
    }

    if (!formData.invoiceId.trim()) {
      newErrors.invoiceId = 'Invoice/Transaction ID is required';
    }

    // Check if at least one of credit or debit has a value
    const hasCredit = formData.credit.trim() && formData.credit !== "0.0" && formData.credit !== "0";
    const hasDebit = formData.debit.trim() && formData.debit !== "0.0" && formData.debit !== "0";

    if (!hasCredit && !hasDebit) {
      newErrors.credit = 'Either Credit or Debit amount is required';
      newErrors.debit = 'Either Credit or Debit amount is required';
    } else {
      // Validate credit if it has a value
      if (hasCredit) {
        const creditAmount = parseFloat(formData.credit.replace(/[€,]/g, ''));
        if (isNaN(creditAmount) || creditAmount <= 0) {
          newErrors.credit = 'Please enter a valid positive credit amount';
        }
      }

      // Validate debit if it has a value
      if (hasDebit) {
        const debitAmount = parseFloat(formData.debit.replace(/[€,]/g, ''));
        if (isNaN(debitAmount) || debitAmount <= 0) {
          newErrors.debit = 'Please enter a valid positive debit amount';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Determine amount and type from credit/debit fields
      const hasCredit = formData.credit.trim() && formData.credit !== "0.0" && formData.credit !== "0";
      const hasDebit = formData.debit.trim() && formData.debit !== "0.0" && formData.debit !== "0";
      
      let amount = 0;
      let type = 'Credit';
      
      if (hasCredit) {
        amount = parseFloat(formData.credit.replace(/[€,]/g, ''));
        type = 'Credit';
      } else if (hasDebit) {
        amount = parseFloat(formData.debit.replace(/[€,]/g, ''));
        type = 'Debit';
      }

      const cleanedData = {
        selectedCustomerId: selectedCustomerId || customerId || null,
        
        date: formData.date,
        invoiceId: formData.invoiceId,
        type: type,
        details: formData.details.trim(),
        amount: amount
      };

      await onSave(cleanedData);
    } catch (error) {
      console.error('Error saving finance transaction:', error);
      alert('An error occurred while saving the transaction. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="finance-modal-overlay">
      <div className="finance-modal no-scrollbar">
        <div className="finance-modal-header">
          <div className="finance-modal-header-content">
            <h3 className="finance-modal-title">
              {isEditing ? 'Edit Finance History' : 'Add New Transaction'}
            </h3>
            <p className="finance-modal-subtitle">
              {isEditing ? 'Edit and update finance history details below' : 'Fill in the transaction details below to add new transaction'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="finance-modal-form">
          <div className="finance-modal-body">
            <div className="finance-form-group">
              <label htmlFor="date" className="finance-form-label">
                Date *
              </label>
              <div className="finance-input-container">
                <Calendar size={16} className="finance-input-icon" />
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className={`finance-input ${errors.date ? 'finance-input-error' : ''}`}
                  disabled
                />
              </div>
              {errors.date && <div className="finance-error-message">{errors.date}</div>}
            </div>

            <div className="finance-form-group">
              <label htmlFor="customerSelect" className="finance-form-label">
                Customer *
              </label>
              <select
                id="customerSelect"
                name="customerSelect"
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                className={`finance-input ${errors.customer ? 'finance-input-error' : ''}`}
                disabled
              >
                <option value="">Select customer</option>
                {customerOptions.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
              </select>
              {errors.customer && <div className="finance-error-message">{errors.customer}</div>}
            </div>

            <div className="finance-form-group">
              <label htmlFor="invoiceId" className="finance-form-label">
                Invoice/Transaction ID *
              </label>
              <input
                type="text"
                id="invoiceId"
                name="invoiceId"
                value={formData.invoiceId}
                onChange={handleInputChange}
                className={`finance-input ${errors.invoiceId ? 'finance-input-error' : ''}`}
                placeholder="Enter invoice/transaction ID"
                required
              />
              {errors.invoiceId && <div className="finance-error-message">{errors.invoiceId}</div>}
            </div>

            <div className="finance-form-group">
              <label htmlFor="credit" className="finance-form-label">
                Credit *
              </label>
              <input
                type="text"
                id="credit"
                name="credit"
                value={formData.credit}
                onChange={handleInputChange}
                className={`finance-input ${errors.credit ? 'finance-input-error' : ''}`}
                placeholder="€0.0"
                disabled={formData.debit.trim() && formData.debit !== "0.0" && formData.debit !== "0"}
              />
              {errors.credit && <div className="finance-error-message">{errors.credit}</div>}
            </div>

            <div className="finance-form-group">
              <label htmlFor="debit" className="finance-form-label">
                Debit *
              </label>
              <input
                type="text"
                id="debit"
                name="debit"
                value={formData.debit}
                onChange={handleInputChange}
                className={`finance-input ${errors.debit ? 'finance-input-error' : ''}`}
                placeholder="€0.0"
                disabled={formData.credit.trim() && formData.credit !== "0.0" && formData.credit !== "0"}
              />
              {errors.debit && <div className="finance-error-message">{errors.debit}</div>}
            </div>

            <div className="finance-form-group">
              <label htmlFor="details" className="finance-form-label">Details</label>
              <textarea
                id="details"
                name="details"
                value={formData.details}
                onChange={handleInputChange}
                className="finance-textarea"
                placeholder="Here we will write details....."
                rows={3}
              />
            </div>
          </div>

          <div className="finance-modal-footer">
            <button
              type="button"
              className="finance-btn finance-btn-cancel"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="finance-btn finance-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="finance-spinner"></span>
                  Saving...
                </>
              ) : (
                isEditing ? 'Update Finance' : 'Add Transaction'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFinanceTransactionForm;
